import { useRef } from 'react';
import { SECTION_IDS, site } from '../content/site';
import { Button } from '../components/ui/Button';
import { SunHalo } from '../components/hero/SunHalo';
import { MOTION_OK, SplitText, gsap, useGSAP } from '../lib/gsap';

/** Weight used by the headline. Lines must be measured once this face is ready. */
const DISPLAY_FACE = '700 1em "Cabinet Grotesk"';

/**
 * Resolves when the headline face is loaded (or after 0.7s on a slow network: the
 * font is preloaded, so this is only a safety net that keeps LCP early).
 * SplitText's autoSplit re-splits if the font lands later, so a timeout is safe.
 */
function displayFontReady(): Promise<unknown> {
	if (!('fonts' in document)) return Promise.resolve();
	return Promise.race([
		Promise.all([document.fonts.load(DISPLAY_FACE), document.fonts.ready]),
		new Promise((resolve) => window.setTimeout(resolve, 700)),
	]).catch(() => undefined);
}

/**
 * Masked line reveal. Each visual line gets 0.14em of padding inside its mask
 * (taken back by a negative margin) so accents and descenders are never clipped.
 * The h1 is a flex column, so those margins cannot collapse and the leading after
 * the split is exactly the leading before it.
 */
const LINE_MASKS =
	'[&_.hero-line]:block [&_.hero-line]:py-[0.14em] [&_.hero-line-mask]:-my-[0.14em] [&_.hero-line-mask]:block';

/** The phrase of the subtitle said in the italic aside voice (brand value: human control). */
const ASIDE = /sous\s+contrôle\s+humain/;

/** Subtitle with its aside set in italic, or the plain text if the copy changed. */
function Subtitle({ text }: { text: string }) {
	const match = ASIDE.exec(text);
	if (!match) return <>{text}</>;
	const start = match.index;
	const end = start + match[0].length;
	return (
		<>
			{text.slice(0, start)}
			<em className="type-aside text-graphite dark:text-mineral">{text.slice(start, end)}</em>
			{text.slice(end)}
		</>
	);
}

/**
 * Hero, "Editorial Split": the text holds the left 8 columns, the right side is
 * left empty for the fiber bundle that rises from the bottom-right to the solar
 * point. Four elements only: headline, subtitle, two CTAs.
 */
export function Hero() {
	const root = useRef<HTMLElement>(null);
	const [firstLine, secondLine] = site.hero.titleLines;
	const cut = secondLine.lastIndexOf(' ');
	const secondLineHead = secondLine.slice(0, cut);
	const emphasis = secondLine.slice(cut + 1);

	useGSAP(
		() => {
			const section = root.current;
			if (!section) return;
			const q = gsap.utils.selector(section);
			const title = q('h1');
			const subtitle = q('[data-hero-subtitle]');
			const ctas = q('[data-hero-cta]');

			// Reduced motion needs no branch: the markup already is the final state (no
			// hiding, no split). The light is static CSS (fades in with a one-time CSS animation).
			const mm = gsap.matchMedia();
			mm.add(MOTION_OK, (context) => {
				let cancelled = false;
				gsap.set([...title, ...subtitle, ...ctas], { autoAlpha: 0 });

				// Entrance, split on real font metrics. Returning the timeline lets
				// autoSplit re-split on resize or late fonts without replaying it.
				const reveal = context.add('reveal', () => {
					// aria "none": the words stay real text for assistive tech (the default
					// would move them into an aria-label, which is not allowed on every element).
					SplitText.create(title, {
						type: 'lines',
						mask: 'lines',
						linesClass: 'hero-line',
						tag: 'span',
						aria: 'none',
						// Keep the French non-breaking spaces (the default would turn them into normal spaces).
						reduceWhiteSpace: false,
						autoSplit: true,
						onSplit: (split: SplitText) =>
							gsap
								.timeline()
								.set(title, { autoAlpha: 1 })
								.from(split.lines, { yPercent: 100, duration: 1.2, stagger: 0.08 }, 0)
								// The solar mark is drawn under the last word once its line has landed.
								.fromTo(title, { '--mark': 0 }, { '--mark': 1, duration: 0.9, ease: 'power2.inOut' }, 0.95)
								.fromTo(subtitle, { autoAlpha: 0, y: 24 }, { autoAlpha: 1, y: 0, duration: 1.1 }, 0.38)
								.fromTo(
									ctas,
									{ autoAlpha: 0, y: 20 },
									{ autoAlpha: 1, y: 0, duration: 1, stagger: 0.07 },
									0.52,
								),
					});
				});
				displayFontReady().then(() => {
					if (!cancelled) reveal();
				});

				return () => {
					cancelled = true;
				};
			});
		},
		{ scope: root },
	);

	return (
		<section
			ref={root}
			id={SECTION_IDS.hero}
			className="relative grid min-h-[100dvh] content-start pb-16 pt-28 sm:pt-32 lg:content-center lg:pb-[18vh] lg:pt-24"
		>
			<SunHalo />

			<div className="container-site grid grid-cols-12 gap-x-6">
				<div className="col-span-12 lg:col-span-8">
					{/* Each title line is its own block and never wraps from 1024px. Sizes are
					    measured: the longest line is 9.93em wide, so 5.4rem still fits 8 columns
					    of the 1400px container, and 8.4vw fits the full width below 1024px. */}
					{/* type-display: optical tracking and left alignment (src/styles/brand.css).
					    type-mark: the solar stroke drawn under the last word. */}
					<h1
						className={`type-display flex flex-col text-[clamp(2.75rem,8.4vw,4.4rem)] font-bold leading-[0.95] text-graphite text-balance dark:text-mineral lg:text-[clamp(2.75rem,5.4vw,5.4rem)] ${LINE_MASKS}`}
					>
						<span className="block lg:whitespace-nowrap">{firstLine}</span>{' '}
						<span className="block lg:whitespace-nowrap">
							{secondLineHead} <span className="type-mark text-forest dark:text-solar">{emphasis}</span>
						</span>
					</h1>

					<p
						data-hero-subtitle
						className="mt-7 max-w-[34rem] text-lg leading-[1.55] text-graphite-muted text-pretty dark:text-dusk-muted lg:mt-8 lg:text-xl lg:leading-[1.5]"
					>
						<Subtitle text={site.hero.subtitle} />
					</p>

					{/* Phones: one column of full-width pills. From 640px: side by side. */}
					<div className="mt-10 grid gap-3 sm:flex sm:flex-wrap sm:items-center">
						<span data-hero-cta className="grid sm:inline-flex">
							<Button variant="primary" href={`#${SECTION_IDS.contact}`} arrow="go">
								{site.hero.primaryCta}
							</Button>
						</span>
						<span data-hero-cta className="grid sm:inline-flex">
							<Button variant="secondary" href={`#${SECTION_IDS.work}`}>
								{site.hero.secondaryCta}
							</Button>
						</span>
					</div>
				</div>
			</div>
		</section>
	);
}
